import { ReactElement } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import { DateTime } from "luxon";
import {
  IAddition,
  IDelayBase, IEAFProduction,
  IProductionExtended,
  ISample, IStrandProduction,
  IVariableGroup
} from "../../../types";
import KeyValueItem from "../../../components/charts/keyValueItem";
import { formatNumber, formatTimeWithSeconds, getCurrentDatetimeInSeconds, toTons } from "../../../system/utils";
import {
  xAdditionAccessorScaled, xProductionBeginAccessorScaled, xProductionEndAccessorScaled,
  xSampleAccessorScaled
} from "../utils";
import { getPalette } from "../../../configuration/configuration";
import HtmlTooltip from "../../../components/charts/tooltip";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import AdditionsLayerGantt from "./additionsLayerGantt";
import VariablesLayerGantt from "../variablesLayerGantt";
import SamplesLayerGantt from "./samplesLayerGantt";

interface IHeatChartProps {
  width?: number;
  productionData: IProductionExtended;
  hideAxisTitle?: boolean;
  selected: IDelayBase | null;
}


const HeatChart = ({ width, productionData, hideAxisTitle, selected }: IHeatChartProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    width,
    height: 60,
    marginBottom: 20,
    marginLeft: 30,
    marginRight: 30,
    marginTop: 10
  });

  const xScale = d3.scaleLinear()
    .domain([productionData.beginDatetime, productionData.endDatetime ?? DateTime.now().toSeconds()])
    .range([0, dimensions.boundedWidth]);


  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IDelayBase) => xScale(d.endDatetime ?? DateTime.now().toSeconds()) - xScale(d.beginDatetime);

  const heatKey = useUniqueId("heat-chart");
  const heatY = 0;

  const variables: IVariableGroup[] = [];
  productionData.variables.forEach(variable => {
    const item = variables.find(x => x.datetime === variable.datetime);
    if (!item) {
      variables.push({
        datetime: variable.datetime,
        variables: [ variable ]
      });
    } else {
      item.variables.push(variable);
    }
  });

  const isHeat = productionData.__typename === "EAFProduction";

  const heatData = isHeat ? productionData as IEAFProduction : null;
  const rmData = !isHeat ? productionData as IStrandProduction : null;

  let totalDelays = 0;
  let barInMill = 0;
  if(rmData) {
    rmData.delays.filter(xyz => !xyz.isDownDay).forEach(delay => {
      totalDelays += delay.duration ?? 0;
    });

    barInMill = ((rmData.endDatetime ?? getCurrentDatetimeInSeconds()) - rmData.beginDatetime - totalDelays) / 60;
  }

  return (
    <div ref={ref}>
      <Chart dimensions={dimensions}>
        <AxisHorizontal
          chartID={heatKey}
          dimensions={dimensions}
          scale={xScale}
          label={!hideAxisTitle ? `${productionData.title}` : ""}
          ticks={getTicks(xScale.domain(), dimensions.boundedWidth > 400 ? 5 : 3)}
          formatTick={(value) => DateTime.fromSeconds(value as number).toLocaleString(DateTime.TIME_WITH_SECONDS)}/>

        <g>

          <HtmlTooltip title={
            <div>
              <div className="display-flex-column align-items-center" >
                <strong>Detail</strong>
                <strong>{productionData.title}</strong>
              </div>
              <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(productionData.beginDatetime as number)} />
              <KeyValueItem name={"End"} value={formatTimeWithSeconds(productionData.endDatetime as number)} />

              {heatData &&
              <>
                <KeyValueItem name={"Steel"} value={heatData.steel.code}/>
                <KeyValueItem name={"Weight"} value={formatNumber(toTons(heatData.weight), 2)} unit="Ton" />
                <KeyValueItem name={"Power ON"} value={formatNumber(heatData.powerON, 2)} unit="min"/>
                <KeyValueItem name={"Power OFF"} value={formatNumber(heatData.powerOFF, 2)} unit="min"/>
                <KeyValueItem name={"Tap to Tap"} value={formatNumber(heatData.tapToTap / 60, 2)} unit="min"/>
              </>
              }
              {rmData &&
                <>
                  <KeyValueItem name={"Bar in Mill"} value={barInMill.toFixed(1)} unit="min"/>
                  <KeyValueItem name={"Heat Number"} value={rmData.heatNumber}/>
                  <KeyValueItem name={"Steel"} value={rmData.steelCode}/>
                </>
              }
            </div>
          }>
            <g>
              <rect
                fill={getPalette().statusColor.production.background}
                x={xProductionBeginAccessorScaled(productionData, xScale)}
                y={heatY}
                width={xProductionEndAccessorScaled(productionData, xScale) - xProductionBeginAccessorScaled(productionData, xScale)}
                height={20}
              />
            </g>
          </HtmlTooltip>

          <DelayLayerGantt delays={productionData.delays} height={20} y={heatY} xAccessorScaled={xAccessorScaled}
            widthAccessorScaled={widthAccessorScaled} selected={selected} chartKey={heatKey}  />

          <SamplesLayerGantt chartID={heatKey} title={productionData.title}
            samples={productionData.samples.filter((x: ISample) => x.sampleType.code.startsWith("F"))}
            y={heatY - 13} xAccessorScaled={sample => xSampleAccessorScaled(sample, xScale) - 4} />

          <VariablesLayerGantt chartID={heatKey} title={productionData.title} variableGroups={variables} y={heatY - 13} xAccessorScaled={variableGroup => xScale(variableGroup.datetime) - 5} />

          {heatData &&
          <AdditionsLayerGantt chartID={heatKey} title={productionData.title}
            additions={heatData.additions}
            xAccessorScaled={(addition: IAddition) => xAdditionAccessorScaled(addition, xScale)}
            y={heatY - 12}/>}

          <text y={heatY + 15}
            x={xProductionBeginAccessorScaled(productionData, xScale) + (xProductionEndAccessorScaled(productionData, xScale) - xProductionBeginAccessorScaled(productionData, xScale)) / 2 }
            fontSize="1em"
            textAnchor="middle"
            fill={"white"}
            fontWeight={"bold"}>
            {productionData.title}
          </text>
        </g>
      </Chart>
    </div>
  );
};

export default HeatChart;
